// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { ReactNode, useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ToastOutlet, AppIcon, Badge, Section, LogoutModal } from '@loa/ui-components';
import { useAdvisor } from '../contexts/AdvisorContext';
import { useTags } from '../contexts/TagsContext';
import { NotificationBell } from './NotificationBell';
import PoweredByFinerty from './PoweredByFinerty';
import PoweredByFinertyMark from './PoweredByFinertyMark';
import { Breadcrumb } from './HeaderLayoutSectionTitle';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { useBranding } from '../contexts/BrandingContext';

type LayoutProps = Readonly<{
  children: ReactNode;
}>;

export function Layout({ children }: LayoutProps) {
  const { advisor, isSupport } = useAdvisor();
  const { featureEnabled: tagsFeatureEnabled } = useTags();
  const { branding } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const { breadcrumbTitle, breadcrumbItems, backTo, hideBreadcrumbBack } = usePageHeader();
  // SSO-only nav items (Calendar + Email). hasIntegration is true when the
  // advisor has a row in advisor_integration_connections, which only exists
  // for advisors who signed in via an external IdP (Google / Microsoft).
  // Password-only advisors never get this and the shortcuts stay hidden.

  const hasBrokerageLogo = branding.logoUrl && branding.logoUrl !== '/logo.svg';

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isDashboardActive = location.pathname === '/';
  const isSupportDashboardActive = location.pathname === '/support-dashboard';
  const isRelationshipsActive = location.pathname.startsWith('/relationships') || location.pathname.startsWith('/clients/') || location.pathname.startsWith('/policies/');
  const isFundsActive = location.pathname.startsWith('/funds');
  const isProvidersActive = location.pathname.startsWith('/providers');
  const isNotificationsActive = location.pathname === '/notifications' || location.pathname.startsWith('/notifications');
  const isActivityLogActive = location.pathname === '/activity-log';
  const isSettingsActive = location.pathname === '/settings' || location.pathname.startsWith('/settings/');
  const isTagsAdminActive = location.pathname === '/admin/tags';

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    setIsLogoutModalOpen(true);
  };

  const isWizardPage = location.pathname === '/relationships/new';
  const isSendInvitationsPage = location.pathname === '/relationships/new/send-invitations';
  const isCorporateHoldingsPage = location.pathname === '/relationships/new/corporate-holdings';
  const isRelationshipDetailPage = /^\/relationships\/[^/]+$/.test(location.pathname)
    && location.pathname !== '/relationships/new';
  const isClientDetailPage = /^\/clients\/[^/]+$/.test(location.pathname);
  const isClientRiskPage = /^\/clients\/[^/]+\/risk$/.test(location.pathname);
  const isClientSustainabilityPage = /^\/clients\/[^/]+\/sustainability$/.test(location.pathname);
  const isClientDeepDivePage = /^\/clients\/[^/]+\/deep-dive$/.test(location.pathname);
  const isPolicyDetailPage = /^\/policies\/[^/]+$/.test(location.pathname);
  const isCoAWizardPage = /^\/relationships\/[^/]+\/change-of-agency$/.test(location.pathname);
  const isNewRequestPage = /^\/relationships\/[^/]+\/requests\/new$/.test(location.pathname);
  const isCashflowWorkspacePage = /^\/relationships\/[^/]+\/cashflow(\/[^/]+)?$/.test(location.pathname);
  // All proposal routes under a relationship: the detail page, the pension
  // wizard and the pension builder (with or without a proposal id).
  const isProposalPage = /^\/relationships\/[^/]+\/proposals(\/.*)?$/.test(location.pathname);

  const PAGE_NAME_EXACT: Record<string, string> = {
    '/support-dashboard': 'Support Dashboard',
    '/settings': 'Settings',
    '/activity-log': 'Activity Log',
    '/admin/dashboard': 'Admin Dashboard',
    '/admin/users': 'User Management',
    '/admin/advisors': 'User Management',
    '/admin/loas': 'Letters of Authority',
    '/admin/assignments': 'Assignment History',
    '/support-team': 'Support Team',
  };
  const PAGE_NAME_PREFIX: Array<[string, string]> = [
    ['/relationships', 'Relationships'],
    ['/clients', 'Clients'],
    ['/policies', 'Policies'],
    ['/providers', 'Providers'],
    ['/notifications', 'Notifications'],
  ];
  const getPageName = (): string => {
    const path = location.pathname;
    if (path === '/') return isSupport ? 'Support Dashboard' : 'Dashboard';
    const exact = PAGE_NAME_EXACT[path];
    if (exact) return exact;
    const prefixMatch = PAGE_NAME_PREFIX.find(([prefix]) => path.startsWith(prefix));
    if (prefixMatch) return prefixMatch[1];
    return 'Dashboard';
  };

  const navLinkBase = "flex items-center justify-center gap-[0.8em] py-[0.8em] no-underline font-sans text-body-02 font-medium leading-normal border-l-[7px] border-l-transparent outline-none focus:outline-none";
  const navLinkActive = '!text-purple-primary !bg-lilac-05 justify-center !border-l-purple-primary';
  const navLinkInactive = '!text-black justify-center hover:!bg-grey-06 transition-colors';

  return (
    <div className="flex h-screen overflow-hidden background-white">
      {/* Left Sidebar */}
      <div
        className="w-[72px] bg-white text-black flex flex-col !border-r-0 shadow-[4px_0_20px_0_rgba(0,0,0,0.04)]"
      >
        {/* Logo/Brand */}
        <div className='border-b border-b-[#E1E1E1] bg-white min-h-[80px] border-r-0 !border-r-0 flex items-center justify-center'>
          {hasBrokerageLogo ? (
            <img
              src={branding.logoUrl}
              alt="Brokerage Logo"
              className="w-[32px] h-[10.1333px] object-contain shrink-0"
            />
          ) : (
            <PoweredByFinerty className="w-[32px] h-[10.1333px] object-contain shrink-0" />
          )}
        </div>

        {/* Menu Items */}
        <div className="pt-[2.5em] flex flex-col gap-sm">
          {/* Support Dashboard - Only for support users */}
          {isSupport ? (
            <Link
              to='/support-dashboard'
              className={`${navLinkBase} ${isSupportDashboardActive ? navLinkActive : navLinkInactive}`}
            >
              <AppIcon name='headphones' size={21.5} className="!m-0" />
            </Link>
          ) : (
            <Link
              to='/'
              className={`${navLinkBase} ${isDashboardActive ? navLinkActive : navLinkInactive}`}
            >
              <AppIcon name='dashboard squares' size={21.5} color={isDashboardActive ? '#2D0A6C' : '#141414'} />
            </Link>
          )}

          <Link
            to='/relationships'
            className={`${navLinkBase} ${isRelationshipsActive ? navLinkActive : navLinkInactive}`}
          >
            <AppIcon name='handshake' size={21.5} color={isRelationshipsActive ? '#2D0A6C' : '#141414'} />
          </Link>

          <Link
            to='/providers'
            className={`${navLinkBase} ${isProvidersActive ? navLinkActive : navLinkInactive}`}
            title='Providers'
          >
            <AppIcon name='building' size={21.5} color={isProvidersActive ? '#2D0A6C' : '#141414'} />
          </Link>

          <Link
            to='/funds'
            className={`${navLinkBase} ${isFundsActive ? navLinkActive : navLinkInactive}`}
            title='Fund Center'
          >
            <AppIcon name='chart' size={21.5} color={isFundsActive ? '#2D0A6C' : '#141414'} />
          </Link>

        </div>
        <div className="flex-1" />

        {/* Bottom Menu Items */}
        <div className="border-t border-[#E1E1E1] py-[1.75em] flex flex-col gap-sm">
          <Link
            to='/activity-log'
            className={`${navLinkBase} ${isActivityLogActive ? navLinkActive : navLinkInactive}`}
          >
            <AppIcon name='history outline' size={21.5} color={isActivityLogActive ? '#2D0A6C' : '#141414'} />
          </Link>

          <Link
            to='/notifications'
            className={`${navLinkBase} ${isNotificationsActive ? navLinkActive : navLinkInactive}`}
          >
            <AppIcon name='bell outline' size={21.5} color={isNotificationsActive ? '#2D0A6C' : '#141414'} />
          </Link>
        </div>


        {(!hasBrokerageLogo || branding.showPoweredByBadge) && (
          <div className="p-8 border-t-[0.5px] border-t-[#E1E1E1] flex justify-center">
            <PoweredByFinertyMark />
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden bg-white">
        {/* Top Navigation Bar */}
        <Section
          className="!m-0 py-0 !rounded-none !border-0 !border-b !border-solid !border-b-[#E1E1E1] !shadow-none !px-lg flex justify-between items-center !bg-white !min-h-[80px] h-[80px] bg-white"
        >
          {(() => {
            if (isWizardPage || isSendInvitationsPage || isCorporateHoldingsPage) {
              let items: { label: string; to?: string }[];
              if (isSendInvitationsPage) {
                items = [
                  { label: 'Relationships', to: '/relationships' },
                  { label: 'Add New Relationship', to: '/relationships/new' },
                  { label: 'Send Invitations' },
                ];
              } else if (isCorporateHoldingsPage) {
                items = [
                  { label: 'Relationships', to: '/relationships' },
                  { label: 'Add New Relationship', to: '/relationships/new' },
                  { label: 'Corporate Holdings' },
                ];
              } else {
                items = [
                  { label: 'Relationships', to: '/relationships' },
                  { label: 'Add New Relationship' },
                ];
              }
              return <Breadcrumb items={items} />;
            }
            if (isRelationshipDetailPage && breadcrumbItems) {
              return (
                <Breadcrumb
                  items={breadcrumbItems}
                  backTo={backTo || undefined}
                  hideBackButton={hideBreadcrumbBack}
                />
              );
            }
            if (isRelationshipDetailPage && breadcrumbTitle) {
              return (
                <Breadcrumb
                  items={[
                    { label: 'Relationships', to: '/relationships' },
                    { label: breadcrumbTitle },
                  ]}
                />
              );
            }
            if (
              (isClientDetailPage || isClientRiskPage || isClientSustainabilityPage || isClientDeepDivePage || isPolicyDetailPage || isCoAWizardPage || isNewRequestPage || isCashflowWorkspacePage || isProposalPage) &&
              breadcrumbItems
            ) {
              return <Breadcrumb items={breadcrumbItems} backTo={backTo || undefined} />;
            }
            return (
              <span className="font-sans text-body-02 font-medium leading-normal text-black">
                {getPageName()}
              </span>
            );
          })()}
          <div className="flex items-center gap-6">
            {/* Support Mode Indicator */}
            {isSupport && (
              <Badge color='teal' size='sm' className="!flex !items-center !gap-2">
                <AppIcon name='headphones' className="!m-0" />
                Support Mode
              </Badge>
            )}

            <NotificationBell />


            {/* User Menu Dropdown */}
            <div ref={userMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                aria-haspopup="menu"
                aria-expanded={isUserMenuOpen}
                data-testid="user-menu-trigger"
                className={`flex items-center gap-[0.8em] cursor-pointer py-[0.4em] px-[0.6em] rounded-lg transition-colors bg-transparent border-0 text-left`}
              >
                {advisor?.photoUrl ? (
                  <img
                    src={advisor.photoUrl}
                    alt={`${advisor.firstName} ${advisor.lastName}`}
                    className="w-[45px] h-[45px] rounded-full object-cover"
                  />
                ) : (
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: '#FFDA8B',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ color: '#5A0000', fontSize: '24px', fontWeight: 500, lineHeight: 1 }}>
                      {advisor ? `${advisor.firstName?.charAt(0) || ''}${advisor.lastName?.charAt(0) || ''}` : '?'}
                    </span>
                  </div>
                )}

                <div>
                  <div className="font-bold flex items-center gap-[0.4em] text-body-02">
                    {advisor ? `${advisor.firstName} ${advisor.lastName}` : 'Loading...'}
                    <AppIcon
                      name={isUserMenuOpen ? 'chevron up' : 'chevron down'}
                      className="!m-0 !text-[0.8em]"
                    />
                  </div>
                  <div className="text-body-03 text-gray-500">
                    {isSupport ? 'Support Staff' : (advisor?.role || 'Advisor')}
                  </div>
                </div>
              </button>

              {/* Dropdown Menu */}
              {isUserMenuOpen && (
                <div className="absolute top-full right-0 mt-sm flex w-[200px] p-sm flex-col items-start gap-xs rounded-lg bg-white z-[1000]" style={{ boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.10)' }}>
                  <button
                    type="button"
                    onClick={() => { setIsUserMenuOpen(false); navigate('/settings'); }}
                    className={`flex items-center gap-sm px-md py-sm self-stretch rounded-lg cursor-pointer transition-colors bg-transparent border-0 text-left w-full ${isSettingsActive ? 'bg-lilac-05' : 'hover:bg-[#F5F5F7]'}`}
                  >
                    <AppIcon
                      name="settings gear"
                      size={16}
                      color={isSettingsActive ? '#2D0A6C' : '#141414'}
                      className="flex-shrink-0"
                    />
                    <span className={`font-sans text-body-02 font-normal leading-normal ${isSettingsActive ? 'text-purple-primary' : 'text-black'}`}>Settings</span>
                  </button>

                  {!isSupport && advisor?.role === 'ADMIN' && tagsFeatureEnabled && (
                    <button
                      type="button"
                      onClick={() => { setIsUserMenuOpen(false); navigate('/admin/tags'); }}
                      data-testid="user-menu-item-tags"
                      className={`flex items-center gap-sm px-md py-sm self-stretch rounded-lg cursor-pointer transition-colors bg-transparent border-0 text-left w-full ${isTagsAdminActive ? 'bg-lilac-05' : 'hover:bg-[#F5F5F7]'}`}
                    >
                      <AppIcon
                        name="edit"
                        size={16}
                        color={isTagsAdminActive ? '#2D0A6C' : '#141414'}
                        className="flex-shrink-0"
                      />
                      <span className={`font-sans text-body-02 font-normal leading-normal ${isTagsAdminActive ? 'text-purple-primary' : 'text-black'}`}>Tags</span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex items-center gap-sm px-md py-sm self-stretch rounded-lg cursor-pointer hover:bg-[#F5F5F7] transition-colors bg-transparent border-0 text-left w-full"
                  >
                    <AppIcon
                      name="logout outline"
                      size={16}
                      color="#141414"
                      className="flex-shrink-0"
                    />
                    <span className="text-black font-sans text-body-02 font-normal leading-normal">Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </Section>

        {/* Content */}
        <div className="flex-1 overflow-auto flex flex-col bg-page-bg">
          <ToastOutlet />
          {children}
        </div>
      </div>

      <LogoutModal
        open={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
      />
    </div>
  );
}
